import { ComponentProps } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { components } from 'react-select';
import AutoComplete, {
  AutoCompleteProps,
} from 'src/components/ui/AutoComplete';

export interface FormMultiSelectProps extends AutoCompleteProps {
  name: string;
}

// The menu is portalled to <body>, which React Aria makes inert while a modal
// is open. Marking it as top layer keeps it clickable from inside a dialog.
const TOP_LAYER = { 'data-react-aria-top-layer': 'true' };

const MenuPortal = (
  props: ComponentProps<
    NonNullable<NonNullable<AutoCompleteProps['components']>['MenuPortal']>
  >
) => (
  <components.MenuPortal
    {...props}
    innerProps={{ ...props.innerProps, ...TOP_LAYER }}
  />
);

const FormMultiSelect = ({ name, ...props }: FormMultiSelectProps) => {
  const { control, clearErrors } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { value, onChange, ...rest },
        fieldState: { invalid, error },
      }) => (
        <AutoComplete
          menuPosition="fixed"
          menuPortalTarget={document.body}
          menuShouldScrollIntoView={false}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          {...rest}
          {...props}
          components={{ MenuPortal, ...props.components }}
          data-rhf-name={name}
          // Controlled: `defaultValue` is read once at mount, so anything that
          // writes to the field afterwards — a reset, or the pruning below in
          // CreatorMultiSelect — changed the form value while the select went
          // on showing the old chips.
          value={value ?? []}
          onChange={(list: any) => {
            clearErrors(name);
            onChange(list);
          }}
          isInvalid={invalid}
          errorMessage={error?.message}
          isMulti
        />
      )}
    />
  );
};

export default FormMultiSelect;
